import React, { forwardRef, useState, useCallback } from 'react'
import {
    Box,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    SxProps,
    Theme,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/pro-duotone-svg-icons/faTimes'
import { faExpand } from '@fortawesome/pro-duotone-svg-icons/faExpand'
import { faSyncAlt } from '@fortawesome/pro-duotone-svg-icons/faSyncAlt'
import { faCalendarAlt } from '@fortawesome/pro-duotone-svg-icons/faCalendarAlt'
import { Block, DateFormat, BaseBlockProps } from '../types'
import { BlockEditButton } from './BlockEditButton'

interface BlockToolbarProps {
    kedo: BaseBlockProps['kedo']
    block: Block
    onClose?: (uuid: string) => void
    onMax?: (uuid: string) => void
    onSuccess?: (blockId: number) => void
    fetchData?: () => void
    setFilterAndFetchData?: (key: string, value: string) => void
    sx?: SxProps<Theme>
}

const DATE_FORMAT_OPTIONS: { value: DateFormat; label: string }[] = [
    { value: 'year_month', label: 'year_month' },
    { value: 'quarter', label: 'quarter' },
    { value: 'year', label: 'year' },
]

export const BlockToolbar = forwardRef<HTMLDivElement, BlockToolbarProps>(
    (
        {
            kedo,
            block,
            onClose,
            onMax,
            onSuccess,
            fetchData,
            setFilterAndFetchData,
            sx,
        },
        ref
    ) => {
        const [dateFormat, setDateFormat] = useState<DateFormat | null>(null)
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
        const open = Boolean(anchorEl)

        const handleDateMenuClick = useCallback(
            (event: React.MouseEvent<HTMLButtonElement>) => {
                setAnchorEl(event.currentTarget)
            },
            []
        )

        const handleDateMenuClose = useCallback(() => {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur()
            }
            setAnchorEl(null)
            requestAnimationFrame(() => anchorEl?.focus())
        }, [anchorEl])

        const handleDateFormatSelect = useCallback(
            (format: DateFormat) => {
                handleDateMenuClose()
                setDateFormat(format)
                setFilterAndFetchData?.('dateFormat', format)
            },
            [setFilterAndFetchData, handleDateMenuClose]
        )

        const handleClose = useCallback(() => {
            onClose?.(block.uuid)
        }, [onClose, block.uuid])

        const handleMaximize = useCallback(() => {
            onMax?.(block.uuid)
        }, [onMax, block.uuid])

        const handleRefresh = useCallback(() => {
            fetchData?.()
        }, [fetchData])

        const handleEditSuccess = useCallback(() => {
            onSuccess?.(block.id)
        }, [onSuccess, block.id])

        const showDateFilter = block.settings?.use_creation_date
        const isAdmin = kedo.isAdminOrEnvironmentAdmin()

        return (
            <Box
                ref={ref}
                className="kedo-block-toolbar"
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 0.5,
                    ...sx,
                }}
            >
                {isAdmin && (
                    <BlockEditButton
                        item={block}
                        kedo={kedo}
                        onSuccess={handleEditSuccess}
                    />
                )}

                {fetchData && (
                    <Tooltip title={kedo.t('Refresh')}>
                        <IconButton size="small" onClick={handleRefresh}>
                            <FontAwesomeIcon icon={faSyncAlt} size="sm" />
                        </IconButton>
                    </Tooltip>
                )}

                {showDateFilter && (
                    <>
                        <Tooltip title={kedo.t(dateFormat ?? 'Date format')}>
                            <IconButton
                                size="small"
                                onClick={handleDateMenuClick}
                                aria-controls={
                                    open ? 'date-format-menu' : undefined
                                }
                                aria-haspopup="true"
                                aria-expanded={open ? 'true' : undefined}
                            >
                                <FontAwesomeIcon
                                    icon={faCalendarAlt}
                                    size="sm"
                                />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            id="date-format-menu"
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleDateMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            {DATE_FORMAT_OPTIONS.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    onClick={() =>
                                        handleDateFormatSelect(option.value)
                                    }
                                    selected={dateFormat === option.value}
                                >
                                    {kedo.t(option.label)}
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}

                {onMax && (
                    <Tooltip title={kedo.t('Maximize')}>
                        <IconButton size="small" onClick={handleMaximize}>
                            <FontAwesomeIcon icon={faExpand} size="sm" />
                        </IconButton>
                    </Tooltip>
                )}

                <Tooltip title={kedo.t('Hide')}>
                    <IconButton size="small" onClick={handleClose}>
                        <FontAwesomeIcon icon={faTimes} size="sm" />
                    </IconButton>
                </Tooltip>
            </Box>
        )
    }
)

BlockToolbar.displayName = 'BlockToolbar'
